import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, Table, Tabs } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import LoadingPage from "../../components/LoadingPage";
import { chain, orderBy } from "lodash";

import Moment from "moment";
import { extendMoment } from "moment-range";

import {
  getPipeSchedule,
  GetDateRange,
  convertInchesToCm,
} from "../../libs/utils";

import PickerWithType from "../../libs/components/PickerWithType";
import SelectDateType from "../../libs/components/SelectDateType";

import { getRobotWeld } from "../../features/robot/robotSlice";
const { TabPane } = Tabs;
const moment = extendMoment(Moment);

const filterData = [
  {
    key: 1,
    title: "Last 24 hours",
    value: 1,
    date: moment().subtract(1, "days"),
  },
  {
    key: 2,
    title: "Last 7 days",
    value: 7,
    date: moment().subtract(7, "days"),
  },
  {
    key: 3,
    title: "Last 30 days",
    value: 30,
    date: moment().subtract(30, "days"),
  },
];

function RobotWeldHistory(props) {
  const dispatch = useDispatch();
  const { robotName } = useParams();

  const robotId =
    robotName && robotName.match(/(\d+)/) && robotName.match(/(\d+)/)[0];
  const {
    // weldRun = [],
    tier: { tier },
    isCustomer,
  } = props;

  const weld_run = useSelector((state) => state.robot.weldRun);

  const [activeKey, setActiveKey] = useState("key3");
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [filterButtonKey, setFilterButtonKey] = useState(3); // default view as last 30 days

  const [dateValues, setDateValues] = useState(null);
  const [type, setType] = useState("range");

  const today = moment();
  const lastWeek = moment().subtract(7, "days");

  const activeButtonStyle = {
    color: "#40a9ff",
    borderColor: "#40a9ff",
  };

  const operations = (
    <>
      <Button
        onClick={() => setFilterButtonKey(1)}
        style={filterButtonKey === 1 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 1).title}
      </Button>
      <Button
        onClick={() => setFilterButtonKey(2)}
        style={filterButtonKey === 2 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 2).title}
      </Button>
      <Button
        onClick={() => setFilterButtonKey(3)}
        style={filterButtonKey === 3 ? activeButtonStyle : {}}
      >
        {filterData.find((item) => item.key === 3).title}
      </Button>
    </>
  );

  const customOperations = (
    <>
      <SelectDateType
        value={type}
        onChange={(value) => {
          setType(value);
          if (type === "range") {
            // set to the default
            setDateValues([lastWeek, today]);
          }
        }}
      ></SelectDateType>
      <PickerWithType
        type={type}
        value={dateValues}
        defaultValue={[lastWeek, today]}
        onChange={(value) => setDateValues(value)}
      />
      <Button
        disabled={!dateValues}
        onClick={() => {
          const dateRange = GetDateRange(type, dateValues);
          dispatch(
            getRobotWeld(robotId, {
              start: dateRange[0],
              end: dateRange[1],
            })
          );
        }}
        icon={<ReloadOutlined />}
      ></Button>
    </>
  );

  useEffect(() => {
    if (!(isCustomer && tier === "lite")) {
      dispatch(
        getRobotWeld(robotId, {
          start: lastWeek.format("YYYY-MM-DD"),
          end: today.format("YYYY-MM-DD"),
        })
      );
    }
  }, [dispatch]);

  if (!weld_run && !(weld_run && weld_run[robotId])) {
    return <LoadingPage></LoadingPage>;
  }

  const weldRun = weld_run[robotId];

  const isMetric =
    weldRun && weldRun[0] && weldRun[0].units_system === "metric";

  const weldRunFilters = (column) => {
    return weldRun
      .filter((item) => {
        return moment(item.arc_on_utc).isSameOrAfter(
          filterData
            .find((i) => i.key === filterButtonKey)
            .date.format("YYYY-MM-DD")
        );
      })
      .map((item) => item[column])
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((item) => {
        return { text: item, value: item };
      });
  };

  const weldRunColumns = (isParent) =>
    [
      // { title: "" },
      {
        title: "Date",

        render: (_, item) => {
          if (item.children) {
            return <span>{moment(item.arc_off_utc).format("YYYY-MM-DD")}</span>;
          } else {
            return <span>{moment(item.arc_on_utc).format("YYYY-MM-DD")}</span>;
          }
        },
      },
      {
        title: "Order",
        dataIndex: "work_order_ref",
        filters: weldRunFilters("work_order_ref"),
        onFilter: (value, record) => record.work_order_ref.indexOf(value) === 0,
      },
      {
        title: "Spool",
        dataIndex: "spool_ref",
        filters: weldRunFilters("spool_ref"),
        onFilter: (value, record) => record.spool_ref.indexOf(value) === 0,
      },
      !isParent && {
        title: "Welder ID",
        dataIndex: "welder_id_ref",
        // filters: weldRunFilters("welder_id_ref"),
        onFilter: (value, record) =>
          record.welder_id_ref && record.welder_id_ref.indexOf(value) === 0,
      },
      !isParent && {
        title: "Run",
        align: "center",
        dataIndex: "run_number", // should have count
      },
      {
        title: "Start Time",
        align: "center",
        render: (_, item) => {
          return <span>{moment(item.arc_on_utc).format("HH:mm:ss")}</span>;
        },
      },
      {
        title: "Arc Off Time",
        align: "center",
        render: (_, item) => {
          if (item.duration) {
            return (
              <span>
                {Number(item.duration.hours()) * 60 +
                  Number(item.duration.minutes()) +
                  "m "}
                {item.duration.seconds() + "s"}
              </span>
            );
          } else {
            return <span>{null}</span>;
          }
        },
      },
      {
        title: "Weld Time",
        align: "center",
        render: (_, item) => {
          return (
            <span>
              {Number(moment.duration({ ...item.weld_time }).hours()) * 60 +
                Number(moment.duration({ ...item.weld_time }).minutes()) +
                "m "}
              {moment.duration({ ...item.weld_time }).seconds() + "s"}
            </span>
          );
        },
      },
      {
        title: "Pipe Size",
        align: "center",
        filters: weldRun
          .filter((item) =>
            moment(item.arc_on_utc).isSameOrAfter(
              filterData
                .find(({ key }) => key === filterButtonKey)
                .date.format("YYYY-MM-DD")
            )
          )
          .map((item) => {
            return `${
              isMetric
                ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
                : item.pipe_size_inches
            }${isMetric ? "cm" : "in"}`;
          })
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((item) => {
            return { text: item, value: item };
          }),
        onFilter: (value, record) => {
          const unit =
            !record.units_system || record.units_system === "imperial"
              ? "in"
              : "cm";
          return `${record.pipe_size_inches}${unit}` === value;
        },
        render: (_, item) => {
          const unit =
            !item.units_system || item.units_system === "imperial"
              ? "in"
              : "cm";
          return <span>{`${item.pipe_size_inches}${unit}`}</span>;
        },
      },
      !isParent && {
        title: "Pipe Sched",
        align: "center",
        filters: weldRun
          .filter((item) =>
            moment(item.arc_on_utc).isSameOrAfter(
              filterData
                .find(({ key }) => key === filterButtonKey)
                .date.format("YYYY-MM-DD")
            )
          )
          .map((item) => {
            return getPipeSchedule(item.pipe_schedule);
          })
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((item) => {
            return { text: item, value: item };
          }),
        onFilter: (value, record) => {
          return record.pipe_schedule === value;
        },
        render: (_, item) => {
          return <span>{getPipeSchedule(item.pipe_schedule)}</span>;
        },
      },
      {
        title: "FDI",
        align: "center",
        render: (_, item) => {
          if (
            Number(moment.duration({ ...item.weld_time }).asMinutes()) >=
            Number(moment.duration({ minutes: 1 }).asMinutes())
          ) {
            return <span>{item.weld_inches}</span>;
          } else {
            return <span></span>;
          }
        },
      },
      !isParent && {
        title: "Operator",
        align: "center",
        dataIndex: "operator_name",
        // filters: weldRunFilters("operator_name"),
        onFilter: (value, record) => {
          if (record.children && record.children.length > 0) {
            return record.children
              .map((child) => child.operator_name.indexOf(value) === 0)
              .filter((c) => c);
          } else {
            return (
              record.operator_name && record.operator_name.indexOf(value) === 0
            );
          }
        },
      },
      !isParent && {
        title: "Procedure",
        align: "center",
        dataIndex: "procedure_name",
        // filters: weldRunFilters("procedure_name"),
        onFilter: (value, record) =>
          record.procedure_name && record.procedure_name.indexOf(value) === 0,
      },
    ].filter((item) => item);

  const pagination = { hideOnSinglePage: true };

  const groupedWeldRun = chain(weldRun)
    .map((item) => {
      const idx = weldRun.findIndex((i) => i === item);
      if (
        idx < weldRun.length - 1 &&
        moment(weldRun[idx + 1].arc_on_utc, "YYYY-MM-DD").diff(
          moment(item.arc_on_utc, "YYYY-MM-DD"),
          "days"
        ) === 0
      ) {
        const duration = moment.duration(
          moment(item.arc_on_utc).diff(moment(weldRun[idx + 1].arc_off_utc))
        );

        return {
          ...item,
          duration,
          pipe_size_inches: isMetric
            ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
            : item.pipe_size_inches,
          weld_inches: isMetric
            ? convertInchesToCm(item.weld_inches).toFixed(0)
            : item.weld_inches,
        };
      } else {
        return {
          ...item,
          pipe_size_inches: isMetric
            ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
            : item.pipe_size_inches,
          weld_inches: isMetric
            ? convertInchesToCm(item.weld_inches).toFixed(0)
            : item.weld_inches,
        };
      }
    })
    .groupBy((item) => {
      return item.work_order_ref + " " + item.spool_ref;
    })
    .entries()
    .map(([work_order_spool, weld]) => {
      const items = orderBy(
        [...weld],
        [
          (i) => {
            return i.run_number;
          },
        ],
        ["desc"]
      );

      const childrenSize = items.length;
      const first = items[0];
      const last = items[childrenSize - 1];

      return {
        key: work_order_spool,
        items,
        arc_on_utc: last.arc_on_utc,
        arc_off_utc: first.arc_off_utc,
        work_order_ref: last.work_order_ref,
        spool_ref: last.spool_ref,

        units_system: last.units_system,
        pipe_size_inches: last.pipe_size_inches,
        pipe_schedule: last.pipe_schedule,

        // sum of arc off time (setup time) on grouped weld
        duration: items.slice(1).reduce((prev, curr) => {
          return prev.add(curr.duration);
        }, moment.duration(first.duration)),

        // sum of weld time on grouped weld
        weld_time: items.slice(1).reduce((prev, curr) => {
          return prev.add(curr.weld_time);
        }, moment.duration(first.weld_time))._data,

        // sum of weld inches with more than 1 min weld time
        weld_inches: items
          .filter((item) => {
            return (
              Number(moment.duration({ ...item.weld_time }).asMinutes()) >=
              Number(moment.duration({ minutes: 1 }).asMinutes())
            );
          })
          .reduce((prev, curr) => {
            return prev + curr.weld_inches;
          }, 0)
          .toFixed(1),
      };
    })

    .value();

  return (
    <>
      {!(isCustomer && tier === "lite") ? (
        <Tabs
          activeKey={activeKey}
          size={"small"}
          type="card"
          onTabClick={(key) => setActiveKey(key)}
          tabBarExtraContent={customOperations}
        >
          <TabPane
            tab={"Weld History"}
            key="key3"
            style={{ height: 417, overflowY: "scroll", overflowX: "scroll" }}
          >
            <Table
              className="WeldHistoryList_Table"
              size={"small"}
              columns={weldRunColumns(true)}
              dataSource={groupedWeldRun.filter((item) => {
                if (dateValues) {
                  const dateRange = GetDateRange(type, dateValues);
                  return moment(
                    moment(item.arc_on_utc).utc(true).format("YYYY-MM-DD")
                  ).isBetween(dateRange[0], dateRange[1], undefined, "[]");
                } else {
                  return moment(
                    moment(item.arc_on_utc).utc(true).format("YYYY-MM-DD")
                  ).isBetween(
                    lastWeek.format("YYYY-MM-DD"),
                    today.format("YYYY-MM-DD"),
                    undefined,
                    "[]"
                  );
                }
              })}
              onRow={({ key }) =>
                expandedKeys.includes(key) && { className: "expand-parent" }
              }
              expandable={{
                showExpandColumn: true,
                rowExpandable: (record) => !!record.items,

                onExpand: (expanded, { key }) => {
                  const keys = expandedKeys;
                  setExpandedKeys(
                    expanded ? keys.concat(key) : keys.filter((k) => k !== key)
                  );
                },
                expandedRowRender: (record) => {
                  return (
                    <Table
                      className="WeldHistoryList_Table WeldHistoryList_Table_Nested"
                      size={"small"}
                      columns={weldRunColumns()}
                      dataSource={record.items}
                      pagination={{
                        ...pagination,
                        pageSize: record.items.length,
                      }}
                      expandable={{
                        showExpandColumn: false,
                      }}
                    />
                  );
                },
              }}
              pagination={{ ...pagination, pageSize: 50 }}
            />
          </TabPane>
        </Tabs>
      ) : (
        <Tabs
          activeKey={activeKey}
          size={"small"}
          type="card"
          onTabClick={(key) => setActiveKey(key)}
          tabBarExtraContent={operations}
        >
          <TabPane
            tab={"Weld History"}
            key="key3"
            style={{ height: 417, overflowY: "scroll", overflowX: "scroll" }}
          >
            <Table
              className="WeldHistoryList_Table"
              size={"small"}
              columns={weldRunColumns(true)}
              dataSource={groupedWeldRun.filter((item) => {
                return moment(item.arc_on_utc).isSameOrAfter(
                  filterData
                    .find((i) => i.key === filterButtonKey)
                    .date.format("YYYY-MM-DD")
                );
              })}
              onRow={({ key }) =>
                expandedKeys.includes(key) && { className: "expand-parent" }
              }
              expandable={{
                showExpandColumn: true,
                rowExpandable: (record) => !!record.items,

                onExpand: (expanded, { key }) => {
                  const keys = expandedKeys;
                  setExpandedKeys(
                    expanded ? keys.concat(key) : keys.filter((k) => k !== key)
                  );
                },
                expandedRowRender: (record) => {
                  return (
                    <Table
                      className="WeldHistoryList_Table WeldHistoryList_Table_Nested"
                      size={"small"}
                      columns={weldRunColumns()}
                      dataSource={record.items}
                      pagination={{
                        ...pagination,
                        pageSize: record.items.length,
                      }}
                      expandable={{
                        showExpandColumn: false,
                      }}
                    />
                  );
                },
              }}
              pagination={{ ...pagination, pageSize: 50 }}
            />
          </TabPane>
        </Tabs>
      )}
    </>
  );
}

export default RobotWeldHistory;
